import { __spreadArrays } from "tslib";
import { evaluateHierarchyConditions } from './evaluateHierarchyConditions';
import { extractLocalizedString } from './i18nLogic';
import { getDisplayablePrice, hasVariationSelectableChoices, } from './menuLogic';
import { calcOrderItemGrandTotal, isUniqueOrderItem } from './orderItemLogic';
import { UnavailabileVariationReason } from '..';
export function getDisplayableOrderItem(_a) {
    var menu = _a.menu, orderItem = _a.orderItem, itemsHash = _a.itemsHash, locale = _a.locale, dispatchTime = _a.dispatchTime, platform = _a.platform, currency = _a.currency, dispatchType = _a.dispatchType, sectionHint = _a.sectionHint;
    var item = itemsHash[orderItem.itemId];
    var conditionResult = evaluateHierarchyConditions({
        entity: item,
        menuRoot: menu,
        conditionParams: { dispatchTime: dispatchTime, platform: platform, dispatchType: dispatchType },
        sectionHint: sectionHint,
        itemsHash: itemsHash,
    });
    var displayableVariations = getDisplayableVariations({
        orderItem: orderItem,
        itemsHash: itemsHash,
        locale: locale,
        currency: currency,
        dispatchTime: dispatchTime,
        platform: platform,
        dispatchType: dispatchType,
        disabled: !conditionResult.result,
        menu: menu,
    });
    var variationsChoicesStrings = getVariationsChoicesStrings(displayableVariations, locale, currency);
    var isDishDeleted = checkIfDishIsOnlyInTrash(menu, orderItem.itemId);
    var variationsWithErrors = displayableVariations.filter(function (variation) { var _a; return ((_a = variation.error) === null || _a === void 0 ? void 0 : _a.hasEnoughSelectableChoices) === false; });
    var unavailabilityErrorReason;
    if (variationsWithErrors.length > 0) {
        unavailabilityErrorReason = variationsWithErrors.some(function (variation) { var _a, _b; return (_b = (_a = variation.error) === null || _a === void 0 ? void 0 : _a.reasons) === null || _b === void 0 ? void 0 : _b.includes(UnavailabileVariationReason.SoldOut); })
            ? UnavailabileVariationReason.SoldOut
            : UnavailabileVariationReason.Unavailable;
    }
    return {
        id: item.id,
        title: extractLocalizedString(item.title, locale),
        subTitle: extractLocalizedString(item.description, locale),
        labels: item.labels || [],
        quantity: orderItem.count || 1,
        specialRequest: orderItem.comment || '',
        errors: conditionResult.reasons,
        variations: displayableVariations,
        submitDisabled: !conditionResult.result || Boolean(unavailabilityErrorReason),
        totalPrice: getDisplayablePrice(calcOrderItemGrandTotal(orderItem), locale, currency),
        price: item.price ? getDisplayablePrice(item.price, locale, currency) : '',
        variationsChoicesStrings: variationsChoicesStrings,
        isDishDeleted: isDishDeleted,
        uniqueItemId: isUniqueOrderItem(orderItem)
            ? orderItem.uniqueItemId
            : undefined,
        unavailabilityErrorReason: unavailabilityErrorReason,
        hideSpecialRequest: item.hideSpecialRequest,
    };
}
function checkIfDishIsOnlyInTrash(menu, orderItemId) {
    // filter all the sections that include orderItemId
    var sectionsWithTheItem = menu.sections.filter(function (section) {
        var _a, _b;
        return ((_a = section.itemIds) === null || _a === void 0 ? void 0 : _a.includes(orderItemId)) || ((_b = section.children) === null || _b === void 0 ? void 0 : _b.some(function (s) { var _a; return (_a = s.itemIds) === null || _a === void 0 ? void 0 : _a.includes(orderItemId); }));
    });
    return (sectionsWithTheItem.length === 1 && sectionsWithTheItem[0].id === 'trash');
}
function getDisplayableVariationType(variation) {
    return variation.minNumAllowed === 1 && variation.maxNumAllowed === 1
        ? 'variation.radio'
        : 'variation.checkbox';
}
function getClientSideId(variationPath, suffix) {
    if (suffix === void 0) { suffix = ''; }
    var result = "variation-" + variationPath
        .map(function (crumb) { return crumb.value; })
        .join('-');
    if (suffix) {
        return result + '-' + suffix;
    }
    else {
        return result;
    }
}
function getDisplayableVariations(_a) {
    var _b;
    var orderItem = _a.orderItem, itemsHash = _a.itemsHash, _c = _a.initialVariationsPath, initialVariationsPath = _c === void 0 ? [] : _c, locale = _a.locale, currency = _a.currency, dispatchTime = _a.dispatchTime, platform = _a.platform, dispatchType = _a.dispatchType, disabled = _a.disabled, menu = _a.menu;
    return (((_b = orderItem.variations) === null || _b === void 0 ? void 0 : _b.map(function (v, index) {
        var _a;
        var choices = (_a = orderItem.variationsChoices) === null || _a === void 0 ? void 0 : _a[index];
        var variationPath = __spreadArrays(initialVariationsPath, [
            { type: 'index', value: index },
        ]);
        var displayableVariation = getDisplayableVariation(v, variationPath, choices, itemsHash, locale, currency, dispatchTime, platform, dispatchType, menu, disabled);
        return displayableVariation;
    })) || []);
}
function isVariationValid(variation, choices) {
    return (choices.length >= (variation.minNumAllowed || 0) &&
        choices.length <= (variation.maxNumAllowed || 99999));
}
export function getDisplayableVariation(variation, variationPath, choices, itemsHash, locale, currency, dispatchTime, platform, dispatchType, menu, disabled) {
    if (choices === void 0) { choices = []; }
    return {
        type: getDisplayableVariationType(variation),
        title: extractLocalizedString(variation.title, locale),
        fullTitle: extractLocalizedString(variation.title, locale),
        limitation: getVariationLimitation(variation),
        min: variation.minNumAllowed || 0,
        max: variation.maxNumAllowed || 99999,
        isValid: isVariationValid(variation, choices),
        variationPath: variationPath,
        clientSideId: getClientSideId(variationPath),
        choices: getDisplayableChoices(variation, choices, itemsHash, variationPath, locale, currency, dispatchTime, platform, dispatchType, menu),
        disabled: !!disabled,
        error: hasVariationSelectableChoices({
            menu: menu,
            variation: variation,
            dispatchTime: dispatchTime,
            platform: platform,
            dispatchType: dispatchType,
            itemsHash: itemsHash,
        }),
    };
}
function getDisplayableChoices(variation, choices, itemsHash, variationPath, locale, currency, dispatchTime, platform, dispatchType, menu) {
    if (choices === void 0) { choices = []; }
    return variation.itemIds.map(function (itemId) {
        var _a;
        var orderItem = choices.find(function (oi) { return oi.itemId === itemId; });
        var isSelected = Boolean(orderItem);
        var price = ((_a = variation.prices) === null || _a === void 0 ? void 0 : _a[itemId]) || 0;
        var item = itemsHash[itemId];
        var _b = getErrorsOfChoice({
            item: item,
            dispatchTime: dispatchTime,
            platform: platform,
            dispatchType: dispatchType,
            menu: menu,
            itemsHash: itemsHash,
        }), errors = _b.errors, isDishDeleted = _b.isDishDeleted;
        var result = {
            itemId: itemId,
            price: price,
            formattedPrice: price ? getDisplayablePrice(price, locale, currency) : '',
            title: extractLocalizedString(itemsHash[itemId].title, locale),
            description: extractLocalizedString(itemsHash[itemId].description, locale),
            clientSideId: getClientSideId(variationPath, itemId),
            isSelected: isSelected,
            isSelectable: errors.length === 0 && !isDishDeleted,
            variations: [],
            errors: errors,
            isDishDeleted: isDishDeleted,
        };
        if (orderItem) {
            result.variations = getDisplayableVariations({
                orderItem: orderItem,
                itemsHash: itemsHash,
                initialVariationsPath: __spreadArrays(variationPath, [
                    { type: 'itemId', value: itemId },
                ]),
                locale: locale,
                currency: currency,
                dispatchTime: dispatchTime,
                platform: platform,
                dispatchType: dispatchType,
                menu: menu,
            });
        }
        return result;
    });
}
export function getVariationLimitation(variation) {
    var _a = variation.minNumAllowed, minNumAllowed = _a === void 0 ? 0 : _a, _b = variation.maxNumAllowed, maxNumAllowed = _b === void 0 ? 0 : _b;
    var min = minNumAllowed;
    var max = maxNumAllowed > 1000 ? 0 : maxNumAllowed;
    if (min === 0 && max === 0) {
        return null;
    }
    else if (min === 0 && max === 1) {
        return 'UP_TO_ONE';
    }
    else if (min === 0 && max > 1) {
        return 'UP_TO_MAX';
    }
    else if (min === 1 && max === 1) {
        return 'EXACTLY_ONE';
    }
    else if (min === 1 && max === 0) {
        return 'AT_LEAST_ONE';
    }
    else if (min > 1 && max === 0) {
        return 'AT_LEAST_MIN';
    }
    else if (min === max) {
        return 'EXACTLY';
    }
    else if (min >= 1 && max > 1) {
        return 'RANGE';
    }
    return null;
}
function getVariationsChoicesStrings(displayableVariations, locale, currency) {
    var result = [];
    displayableVariations.forEach(function (dv) {
        var tmp = [];
        dv.choices.forEach(function (c) {
            if (c.isSelected) {
                var price = c.price
                    ? " (" + getDisplayablePrice(c.price, locale, currency) + ")"
                    : '';
                tmp.push(c.title + price);
            }
            if (c.variations.length > 0) {
                tmp.push.apply(tmp, getVariationsChoicesStrings(c.variations, locale, currency));
            }
        });
        result.push(tmp.join(', '));
    });
    return result.filter(Boolean);
}
export function getErrorsOfChoice(_a) {
    var item = _a.item, dispatchTime = _a.dispatchTime, platform = _a.platform, dispatchType = _a.dispatchType, menu = _a.menu, itemsHash = _a.itemsHash;
    var conditionResult = evaluateHierarchyConditions({
        entity: item,
        menuRoot: menu,
        conditionParams: {
            dispatchTime: dispatchTime,
            platform: platform,
            dispatchType: dispatchType,
        },
        itemsHash: itemsHash,
    });
    return {
        errors: conditionResult.reasons,
        isDishDeleted: checkIfDishIsOnlyInTrash(menu, item.id),
    };
}
//# sourceMappingURL=displayableOrderItem.js.map